import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../components/layout';
import { Alert, Button, Input } from '../../components/core';
import { http } from '../../lib/api';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await http.post('/api/auth/forgot-password', { email });
      setMessage('If an account exists for that email, a reset link is on its way. Check your inbox!');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Reset your password">
      {message ? <Alert kind="success">{message}</Alert> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Input label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" required />
        <Button type="submit" disabled={busy}>Send reset link</Button>
      </form>
      <div className="mt-4 text-muted text-sm">
        <Link to="/login">Back to login</Link>
      </div>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await http.post('/api/auth/reset-password', { token, newPassword: password });
      setMessage('Password updated! You can now log in.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Choose a new password">
      {error ? <Alert kind="danger">{error}</Alert> : null}
      {message ? <Alert kind="success">{message}</Alert> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Input label="New password" type="password" value={password} onChange={setPassword} autoComplete="new-password" required />
        <Button type="submit" disabled={busy}>Update password</Button>
      </form>
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const [state, setState] = useState<'loading' | 'done' | 'error'>('loading');
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  useEffect(() => {
    void http
      .get<{ message: string }>(`/api/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(() => setState('done'))
      .catch(() => setState('error'));
  }, [token]);

  return (
    <AuthLayout title="Email verification">
      {state === 'loading' ? <p className="text-muted">Verifying…</p> : null}
      {state === 'done' ? <Alert kind="success">Email verified — you can now log in.</Alert> : null}
      {state === 'error' ? <Alert kind="danger">Verification link is invalid or expired.</Alert> : null}
      <div className="mt-4">
        <Button onClick={() => navigate('/login')}>Go to login</Button>
      </div>
    </AuthLayout>
  );
}